/**
 * Pure state logic for open orders, order history and fills (#671, #672). No React, no I/O.
 *
 * Stream semantics (docs/research/STREAMS.md S2, spec section 1):
 *   - WS `openOrders` {user, dex:""} pushes the FULL open-order list each frame: replace state wholesale.
 *   - WS `orderUpdates` has no snapshot. status "open" adds an order, every other status removes it.
 *   - WS `userFills`: first frame isSnapshot (last 30), then deltas. `startPosition` is the position BEFORE the
 *     fill, so position after = startPosition + signed size (B = +sz, A = −sz). Dedupe by `tid`.
 *   - WS `userHistoricalOrders`: snapshot (last 30) then each status change. One row per (oid, status).
 */
import type { BasicOrder, FrontendOrder, HistoricalOrder, OrderUpdate, UserFill } from '../../hl/types';
import { t } from '../../i18n/i18n';

// ---------------------------------------------------------------------------------------------
// Exact decimal-string arithmetic (sizes can carry up to 8 decimals; floats would drift).
// ---------------------------------------------------------------------------------------------

function parseDec(s: string): { n: bigint; scale: number } {
  const t = s.trim();
  const m = /^([+-]?)(\d*)(?:\.(\d*))?$/.exec(t);
  if (!m || (m[2] === '' && (m[3] ?? '') === '')) throw new Error(`invalid decimal: ${s}`);
  const frac = m[3] ?? '';
  const n = BigInt(`${m[2] || '0'}${frac}`);
  return { n: m[1] === '-' ? -n : n, scale: frac.length };
}

function formatDec(n: bigint, scale: number): string {
  const neg = n < 0n;
  let digits = (neg ? -n : n).toString();
  if (scale > 0) {
    digits = digits.padStart(scale + 1, '0');
    const int = digits.slice(0, -scale);
    const frac = digits.slice(-scale).replace(/0+$/, '');
    digits = frac ? `${int}.${frac}` : int;
  }
  return neg && digits !== '0' ? `-${digits}` : digits;
}

/** a + b for decimal strings, exact. Result has no trailing zeros ("62.0" + "-15.0" → "47"). */
export function addDec(a: string, b: string): string {
  const x = parseDec(a);
  const y = parseDec(b);
  const scale = Math.max(x.scale, y.scale);
  const xs = x.n * 10n ** BigInt(scale - x.scale);
  const ys = y.n * 10n ** BigInt(scale - y.scale);
  return formatDec(xs + ys, scale);
}

/** Numeric equality of two decimal strings ("47" == "47.0"). */
export function decEq(a: string, b: string): boolean {
  return addDec(a, negDec(b)) === '0';
}

export function negDec(a: string): string {
  const t = a.trim();
  return t.startsWith('-') ? t.slice(1) : `-${t.replace(/^\+/, '')}`;
}

// ---------------------------------------------------------------------------------------------
// Fills
// ---------------------------------------------------------------------------------------------

/** Signed fill size: buy (B) adds, sell (A) subtracts. */
export function signedSize(f: Pick<UserFill, 'side' | 'sz'>): string {
  return f.side === 'B' ? f.sz : negDec(f.sz);
}

/** Position after the fill = startPosition + signed size (spec section 1). */
export function positionAfter(f: Pick<UserFill, 'side' | 'sz' | 'startPosition'>): string {
  return addDec(f.startPosition, signedSize(f));
}

/** Newest first; tid breaks ties so the order is total and stable. */
export function compareFillsDesc(a: UserFill, b: UserFill): number {
  return b.time - a.time || b.tid - a.tid;
}

/** Merge fill lists, dedupe by tid (later copies win), newest first. */
export function mergeFills(existing: readonly UserFill[], incoming: readonly UserFill[]): UserFill[] {
  if (incoming.length === 0) return existing as UserFill[];
  const byTid = new Map<number, UserFill>();
  for (const f of existing) byTid.set(f.tid, f);
  for (const f of incoming) byTid.set(f.tid, f);
  return orderSameTimeRuns([...byTid.values()].sort(compareFillsDesc));
}

/**
 * Several fills of one coin often share a timestamp (one order sweeping levels in a block) and tids are not
 * monotonic, so (time, tid) does not give their real sequence. Within each same-time run, re-order every coin's
 * fills by chaining positionAfter(a) == b.startPosition, which is the true execution order. Unchainable fills
 * keep their (time, tid) order.
 */
function orderSameTimeRuns(desc: UserFill[]): UserFill[] {
  let i = 0;
  while (i < desc.length) {
    let j = i + 1;
    while (j < desc.length && desc[j]!.time === desc[i]!.time) j++;
    if (j - i > 1) reorderRun(desc, i, j);
    i = j;
  }
  return desc;
}

function reorderRun(desc: UserFill[], from: number, to: number): void {
  const slots = new Map<string, number[]>();
  for (let k = from; k < to; k++) {
    const c = desc[k]!.coin;
    const s = slots.get(c);
    if (s) s.push(k);
    else slots.set(c, [k]);
  }
  for (const idx of slots.values()) {
    if (idx.length < 2) continue;
    const group = idx.map((k) => desc[k]!);
    const chain = chainAscending(group);
    // Descending display order = reversed chain.
    chain.reverse().forEach((f, n) => {
      desc[idx[n]!] = f;
    });
  }
}

function chainAscending(group: UserFill[]): UserFill[] {
  const after = new Map<UserFill, string>(group.map((f) => [f, positionAfter(f)]));
  const remaining = new Set(group);
  const out: UserFill[] = [];
  while (remaining.size > 0) {
    // Head: a fill no remaining fill leads into.
    const rem = [...remaining];
    let cur: UserFill | undefined =
      rem.find((f) => !rem.some((g) => g !== f && decEq(after.get(g)!, f.startPosition))) ?? rem[rem.length - 1];
    while (cur) {
      out.push(cur);
      remaining.delete(cur);
      const a = after.get(cur)!;
      cur = [...remaining].find((g) => decEq(g.startPosition, a));
    }
  }
  return out;
}

export interface PositionMismatch {
  coin: string;
  /** Fill whose computed position-after disagrees with the next fill's startPosition. */
  tid: number;
  nextTid: number;
  computed: string;
  nextStart: string;
}

/**
 * For each coin, walk fills oldest → newest and check positionAfter(fill) == next.startPosition.
 * Gaps in our window (fills we have not loaded) show up here too, so only adjacent loaded fills are compared
 * and callers should treat a mismatch as a dev diagnostic, not a user-facing error.
 */
export function findPositionMismatches(fillsDesc: readonly UserFill[]): PositionMismatch[] {
  const last = new Map<string, UserFill>();
  const out: PositionMismatch[] = [];
  for (let i = fillsDesc.length - 1; i >= 0; i--) {
    const f = fillsDesc[i]!;
    const prev = last.get(f.coin);
    if (prev) {
      const computed = positionAfter(prev);
      if (!decEq(computed, f.startPosition)) {
        out.push({ coin: f.coin, tid: prev.tid, nextTid: f.tid, computed, nextStart: f.startPosition });
      }
    }
    last.set(f.coin, f);
  }
  return out;
}

// ---------------------------------------------------------------------------------------------
// Open orders
// ---------------------------------------------------------------------------------------------

/** orderUpdates carry BasicOrder fields only; fill the FrontendOrder extras with neutral defaults. */
export function toFrontendOrder(o: BasicOrder | FrontendOrder): FrontendOrder {
  const f = o as Partial<FrontendOrder>;
  return {
    ...o,
    triggerCondition: f.triggerCondition ?? 'N/A',
    isTrigger: f.isTrigger ?? false,
    triggerPx: f.triggerPx ?? '0.0',
    children: f.children ?? [],
    isPositionTpsl: f.isPositionTpsl ?? false,
    reduceOnly: f.reduceOnly ?? false,
    orderType: f.orderType ?? 'Limit',
    tif: f.tif ?? null,
  };
}

/** Newest first, oid tiebreak. */
export function sortOrdersDesc(orders: FrontendOrder[]): FrontendOrder[] {
  return orders.sort((a, b) => b.timestamp - a.timestamp || b.oid - a.oid);
}

/**
 * Apply orderUpdates to the open-order list: "open" adds (or refreshes size of) the order, any other status
 * removes it (spec section 1). Known richer fields (tif, reduceOnly…) are kept when an order is refreshed.
 */
export function applyOrderUpdates(open: readonly FrontendOrder[], updates: readonly OrderUpdate[]): FrontendOrder[] {
  const byOid = new Map<number, FrontendOrder>();
  for (const o of open) byOid.set(o.oid, o);
  for (const u of updates) {
    if (u.status === 'open') {
      const prev = byOid.get(u.order.oid);
      byOid.set(u.order.oid, prev ? { ...prev, ...u.order } : toFrontendOrder(u.order));
    } else {
      byOid.delete(u.order.oid);
    }
  }
  return sortOrdersDesc([...byOid.values()]);
}

// ---------------------------------------------------------------------------------------------
// Order history
// ---------------------------------------------------------------------------------------------

export function historyKey(h: Pick<HistoricalOrder, 'status'> & { order: { oid: number } }): string {
  return `${h.order.oid}:${h.status}`;
}

/** Order history kept in memory: market makers emit hundreds of status changes a minute. HL REST caps at 2000 too. */
export const HISTORY_MAX = 2000;

/** Merge order-history rows, one per (oid, status), newest status change first, capped at `max` rows. */
export function mergeHistory(
  existing: readonly HistoricalOrder[],
  incoming: readonly HistoricalOrder[],
  max = HISTORY_MAX,
): HistoricalOrder[] {
  if (incoming.length === 0) return existing as HistoricalOrder[];
  const m = new Map<string, HistoricalOrder>();
  for (const h of existing) m.set(historyKey(h), h);
  for (const h of incoming) m.set(historyKey(h), h);
  return [...m.values()]
    .sort((a, b) => b.statusTimestamp - a.statusTimestamp || b.order.oid - a.order.oid || a.status.localeCompare(b.status))
    .slice(0, max);
}

export type StatusTone = 'open' | 'filled' | 'canceled' | 'rejected' | 'other';

/** Human status + reason for a HL order status (rejected variants carry the reason in the name). */
export function describeStatus(status: string): { label: string; reason: string | null; tone: StatusTone } {
  switch (status) {
    case 'open':
      return { label: t('orders.status.open'), reason: null, tone: 'open' };
    case 'filled':
      return { label: t('orders.status.filled'), reason: null, tone: 'filled' };
    case 'canceled':
      return { label: t('orders.status.canceled'), reason: t('orders.reason.by_user'), tone: 'canceled' };
    case 'triggered':
      return { label: t('orders.status.triggered'), reason: null, tone: 'other' };
    case 'marginCanceled':
      return { label: t('orders.status.canceled'), reason: t('orders.reason.margin'), tone: 'canceled' };
    case 'reduceOnlyCanceled':
      return { label: t('orders.status.canceled'), reason: t('orders.reason.reduce_only'), tone: 'canceled' };
    case 'selfTradeCanceled':
      return { label: t('orders.status.canceled'), reason: t('orders.reason.self_trade'), tone: 'canceled' };
    case 'siblingFilledCanceled':
      return { label: t('orders.status.canceled'), reason: t('orders.reason.sibling'), tone: 'canceled' };
    case 'scheduledCancel':
      return { label: t('orders.status.canceled'), reason: t('orders.reason.scheduled'), tone: 'canceled' };
    case 'liquidatedCanceled':
      return { label: t('orders.status.canceled'), reason: t('orders.reason.liquidated'), tone: 'canceled' };
    case 'vaultClosedCanceled':
      return { label: t('orders.status.canceled'), reason: t('orders.reason.vault_closed'), tone: 'canceled' };
    case 'openInterestCapCanceled':
      return { label: t('orders.status.canceled'), reason: t('orders.reason.oi_cap'), tone: 'canceled' };
    case 'delistedCanceled':
      return { label: t('orders.status.canceled'), reason: t('orders.reason.delisted'), tone: 'canceled' };
    case 'rejected':
      return { label: t('orders.status.rejected'), reason: null, tone: 'rejected' };
    case 'insufficientSpotBalanceRejected':
      return { label: t('orders.status.rejected'), reason: t('orders.reason.balance'), tone: 'rejected' };
    case 'badAloPxRejected':
      return { label: t('orders.status.rejected'), reason: t('orders.reason.post_only'), tone: 'rejected' };
    case 'iocCancelRejected':
      return { label: t('orders.status.rejected'), reason: t('orders.reason.ioc'), tone: 'rejected' };
    case 'tickRejected':
      return { label: t('orders.status.rejected'), reason: t('orders.reason.tick'), tone: 'rejected' };
    case 'minTradeNtlRejected':
      return { label: t('orders.status.rejected'), reason: t('orders.reason.min_value'), tone: 'rejected' };
    case 'perpMarginRejected':
      return { label: t('orders.status.rejected'), reason: t('orders.reason.margin'), tone: 'rejected' };
    case 'reduceOnlyRejected':
      return { label: t('orders.status.rejected'), reason: t('orders.reason.reduce_only'), tone: 'rejected' };
    case 'oracleRejected':
      return { label: t('orders.status.rejected'), reason: t('orders.reason.oracle'), tone: 'rejected' };
    default: {
      // Unknown variants: split camelCase "fooBarRejected" → reason "Foo bar".
      const m = /^(.*?)(Rejected|Canceled)$/.exec(status);
      if (m && m[1]) {
        const reason = m[1].replace(/([A-Z])/g, ' $1').toLowerCase().trim();
        const label = m[2] === 'Rejected' ? t('orders.status.rejected') : t('orders.status.canceled');
        return {
          label,
          reason: reason.charAt(0).toUpperCase() + reason.slice(1),
          tone: m[2] === 'Rejected' ? 'rejected' : 'canceled',
        };
      }
      return { label: status, reason: null, tone: 'other' };
    }
  }
}

/** HL caps userFills responses at 2000 rows. */
export const FILLS_PAGE_CAP = 2000;
